import { NgModule, inject } from '@angular/core';
import { RouterModule, Routes, ResolveFn, CanActivateFn } from '@angular/router';
import {
	ROUTE_NAMES,
	getPathTemplate,
	RouterService
} from '@go-modules/services/router/router.service';
import { RouteResolverService } from '@go-modules/services/routing/route-resolver.service';
import { AppDataService } from '@go-modules/services/app-data/app-data.service';
import { WizardRouteViewWrapperComponent } from './wizards/wizard-route-view/wizard-route-view-wrapper.component';

const resolveFoldersRoute: ResolveFn<boolean> = (route) =>
	inject(RouteResolverService).resolveFoldersRoute(route.params);

const resolveFolderRoute: ResolveFn<boolean> = (route) =>
	inject(RouteResolverService).resolveFolderRoute(route.params);

const resolveSessionRoute: ResolveFn<boolean> = (route) =>
	inject(RouteResolverService).resolveSessionRoute(route.params);

const resolveLicenseManagementRoute: ResolveFn<boolean> = (route) =>
	inject(RouteResolverService).resolveLicenseManagementRoute(route.params);

const redirectFoldersGuard: CanActivateFn = () => {
	const appData = inject(AppDataService).getSnapshot();
	const routerService = inject(RouterService);

	if (appData.hasCourseLevelLTI) {
		routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDER_VIEW, { folder_id: appData.group_id });
	} else {
		routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW, {
			activity_id: appData.activity.activity_id
		});
	}

	return false;
};

const ensureCourseLevelGuard: CanActivateFn = () => {
	const appData = inject(AppDataService).getSnapshot();
	if (appData.hasCourseLevelLTI) {
		return true;
	}

	const routerService = inject(RouterService);
	routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW, {
		activity_id: appData.activity.activity_id
	});
	return false;
};

const routes: Routes = [
	{
		path: getPathTemplate(ROUTE_NAMES.DASHBOARD_FOLDERS).replace('dashboard/', ''),
		canActivate: [redirectFoldersGuard],
		loadChildren: () =>
			import('@dashboard/app/components/folders-view/folders-view.dashboard.module').then(
				(m) => m.FoldersViewDashboardModule
			),
		data: { routeName: ROUTE_NAMES.DASHBOARD_FOLDERS },
		resolve: { foldersRouting: resolveFoldersRoute }
	},
	{
		path: getPathTemplate(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW).replace('dashboard/', ''),
		data: { routeName: ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW },
		loadChildren: () =>
			import('@go-modules/components/activity-view/activity-view.dashboard.module').then(
				(m) => m.ActivityViewDashboardModule
			)
	},
	{
		path: getPathTemplate(ROUTE_NAMES.DASHBOARD_SESSION_VIEW).replace('dashboard/', ''),
		data: { routeName: ROUTE_NAMES.DASHBOARD_SESSION_VIEW },
		loadChildren: () =>
			import(
				'@go-modules/components/feedback-session/session-view/session-view.dashboard.module'
			).then((m) => m.SessionViewDashboardModule),
		resolve: { sessionRouting: resolveSessionRoute }
	},
	{
		path: getPathTemplate(ROUTE_NAMES.DASHBOARD_FOLDER_VIEW).replace('dashboard/', ''),
		data: { routeName: ROUTE_NAMES.DASHBOARD_FOLDER_VIEW },
		canActivate: [ensureCourseLevelGuard],
		loadChildren: () =>
			import('@go-modules/components/folder-view/folder-view.dashboard.module').then(
				(m) => m.FolderViewDashboardModule
			),
		resolve: { folderRouting: resolveFolderRoute }
	},
	{
		path: getPathTemplate(ROUTE_NAMES.DASHBOARD_ARCHIVE).replace('dashboard/', ''),
		data: { routeName: ROUTE_NAMES.DASHBOARD_ARCHIVE },
		loadChildren: () =>
			import(
				'@go-modules/components/archive/archive-view/archive-view.dashboard.module'
			).then((m) => m.ArchiveViewDashboardModule)
	},
	{
		path: getPathTemplate(ROUTE_NAMES.DASHBOARD_SETTINGS).replace('dashboard/', ''),
		data: { routeName: ROUTE_NAMES.DASHBOARD_SETTINGS },
		loadChildren: () =>
			import('@go-modules/components/user-settings/user-settings.dashboard.module').then(
				(m) => m.UserSettingsDashboardModule
			)
	},
	{
		path: getPathTemplate(ROUTE_NAMES.LICENSE_MANAGEMENT).replace('dashboard/', ''),
		data: { routeName: ROUTE_NAMES.LICENSE_MANAGEMENT },
		loadChildren: () =>
			import(
				'@go-modules/components/license-management/license-management.dashboard.module'
			).then((m) => m.LicenseManagementDashboardModule),
		resolve: { routeResolver: resolveLicenseManagementRoute }
	},
	{
		path: getPathTemplate(ROUTE_NAMES.LICENSE_SEATS_MANAGEMENT).replace('dashboard/', ''),
		data: { routeName: ROUTE_NAMES.LICENSE_SEATS_MANAGEMENT },
		loadChildren: () =>
			import(
				'@go-modules/components/license-seats-management/license-seats-management.dashboard.module'
			).then((m) => m.LicenseSeatsManagementDashboardModule)
	},
	{
		path: getPathTemplate(ROUTE_NAMES.DASHBOARD_LICENSE_MANAGEMENT_ORG).replace('dashboard/', ''),
		data: { routeName: ROUTE_NAMES.DASHBOARD_LICENSE_MANAGEMENT_ORG },
		loadChildren: () =>
			import(
				'@go-modules/components/license-management/license-management.dashboard.module'
			).then((m) => m.LicenseManagementDashboardModule),
		resolve: { routeResolver: resolveLicenseManagementRoute }
	},
	{
		path: getPathTemplate(ROUTE_NAMES.DASHBOARD_WIZARD).replace('dashboard/', ''),
		data: { routeName: ROUTE_NAMES.DASHBOARD_WIZARD },
		component: WizardRouteViewWrapperComponent
	}
];

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule]
})
export class AppRoutingModule { }
